/**
 * ConfigPanel — Side panel for editing selected node properties.
 * Appears when a node is selected in the canvas.
 */
import React from 'react';
import useBuilderStore from './store/builderStore';

const SERVICE_CONFIG_FIELDS = {
    doqsolv: [
        { key: 'template_id', label: 'Template ID', type: 'text', placeholder: 'e.g. tmpl_abc123' },
    ],
    live_stamping: [
        { key: 'service_id', label: 'Service ID', type: 'text', placeholder: 'e.g. svc_xyz' },
    ],
    esign: [
        { key: 'esign_type', label: 'eSign Type', type: 'select', options: ['aadhaar', 'dsc', 'biometric'] },
        { key: 'days_to_expire', label: 'Days to Expire', type: 'number', placeholder: '30' },
    ],
    mock: [],
};

const ConfigPanel = () => {
    const selectedNodeId = useBuilderStore((s) => s.selectedNodeId);
    const nodes = useBuilderStore((s) => s.nodes);
    const updateNodeData = useBuilderStore((s) => s.updateNodeData);
    const deleteNode = useBuilderStore((s) => s.deleteNode);
    const clearSelection = useBuilderStore((s) => s.clearSelection);

    const node = nodes.find((n) => n.id === selectedNodeId);

    if (!node) return null;

    const data = node.data;
    const configFields = SERVICE_CONFIG_FIELDS[data.serviceType] || [];

    const handleNameChange = (e) => {
        updateNodeData(node.id, { name: e.target.value, label: e.target.value });
    };

    const handleConfigChange = (key, value) => {
        updateNodeData(node.id, {
            config: { ...data.config, [key]: value },
        });
    };

    const handleDelete = () => {
        deleteNode(node.id);
    };

    const serviceColors = {
        doqsolv: '#6366F1',
        live_stamping: '#10B981',
        esign: '#3B82F6',
        mock: '#6B7280',
    };
    const color = serviceColors[data.serviceType] || '#6B7280';

    return (
        <div
            style={{
                width: '280px',
                background: '#1E293B',
                borderLeft: '1px solid rgba(255,255,255,0.06)',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
            }}
        >
            {/* Header */}
            <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <h3 style={{ color: '#F1F5F9', fontSize: '14px', fontWeight: 600, margin: 0 }}>
                        Node Settings
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                        <span style={{ color: '#94A3B8', fontSize: '11px', textTransform: 'uppercase' }}>
                            {data.serviceType}
                        </span>
                    </div>
                </div>
                <button
                    onClick={clearSelection}
                    style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '18px', padding: '4px' }}
                >
                    ✕
                </button>
            </div>

            {/* Form Body */}
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Node Name */}
                <div>
                    <label style={{ color: '#94A3B8', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>
                        Node Name
                    </label>
                    <input
                        value={data.name || ''}
                        onChange={handleNameChange}
                        style={{
                            width: '100%',
                            background: '#0F172A',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            color: '#F1F5F9',
                            fontSize: '13px',
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* Node ID (read-only) */}
                <div>
                    <label style={{ color: '#94A3B8', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>
                        Node ID
                    </label>
                    <input
                        value={node.id}
                        readOnly
                        style={{
                            width: '100%',
                            background: '#0F172A',
                            border: '1px solid rgba(255,255,255,0.06)',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            color: '#64748B',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* Service-specific config fields */}
                {configFields.length > 0 && (
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '16px' }}>
                        <div style={{ color: '#94A3B8', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
                            Service Config
                        </div>
                        {configFields.map((field) => (
                            <div key={field.key} style={{ marginBottom: '12px' }}>
                                <label style={{ color: '#CBD5E1', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                    {field.label}
                                </label>
                                {field.type === 'select' ? (
                                    <select
                                        value={data.config?.[field.key] || ''}
                                        onChange={(e) => handleConfigChange(field.key, e.target.value)}
                                        style={{
                                            width: '100%',
                                            background: '#0F172A',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: '8px',
                                            padding: '8px 12px',
                                            color: '#F1F5F9',
                                            fontSize: '13px',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <option value="">Select...</option>
                                        {field.options.map((opt) => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type={field.type}
                                        value={data.config?.[field.key] || ''}
                                        onChange={(e) => handleConfigChange(field.key, e.target.value)}
                                        placeholder={field.placeholder}
                                        style={{
                                            width: '100%',
                                            background: '#0F172A',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: '8px',
                                            padding: '8px 12px',
                                            color: '#F1F5F9',
                                            fontSize: '13px',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Button */}
            <div style={{ padding: '16px', marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <button
                    onClick={handleDelete}
                    style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#EF4444',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.2)'; }}
                    onMouseLeave={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                >
                    Delete Node
                </button>
            </div>
        </div>
    );
};

export default ConfigPanel;
